<template>
    <div id="oee-page">
        <div class="header-block d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
                <div class="page-title">OEE</div>
                <div class="date-range-info" v-if="dateRangeText && !hasError">{{ dateRangeText }}</div>
                <div class="error-info" v-if="hasError">
                    <feather-icon icon="AlertCircleIcon" size="16" class="mr-25" />
                    Erro ao buscar os dados
                </div>
            </div>

            <b-button class="btn-filters" variant="outline-primary" size="sm" @click="filtersOpen = true">
                <feather-icon icon="FilterIcon" class="mr-50" />
                Filtros
            </b-button>
        </div>

        <div class="body-block">
            <b-row>
                <b-col cols="12" class="charts-col">
                    <!-- KPIs -->

                    <div class="kpi-grid">
                        <div v-for="metric in kpis" :key="metric.key" class="kpi-item">
                            <b-card class="kpi-card cursor-pointer" v-b-tooltip.hover="'Clique para ver detalhamento matemático'" @click="openGlobalDetails(getTabKey(metric.key))">
                                <div class="kpi-title">{{ metric.title }}</div>

                                <div class="kpi-donut-wrapper">
                                    <svg class="kpi-ticks" :width="kpiSize" :height="kpiSize">
                                        <g v-for="(tick, index) in ticks" :key="index">
                                            <line :x1="getTickX1(tick.angle, kpiSize)"
                                                :y1="getTickY1(tick.angle, kpiSize)"
                                                :x2="getTickX2(tick.angle, kpiSize, tick.value)"
                                                :y2="getTickY2(tick.angle, kpiSize, tick.value)"
                                                :stroke="getTickColor(tick.value)" stroke-width="2" />
                                        </g>
                                        <text :x="getLabelX(225, kpiSize)" :y="getLabelY(225, kpiSize)"
                                            class="kpi-label kpi-label-zero" text-anchor="middle">0</text>
                                        <text :x="getLabelX(495, kpiSize)" :y="getLabelY(495, kpiSize)"
                                            class="kpi-label kpi-label-hundred" text-anchor="middle">100</text>
                                    </svg>
                                    <div class="kpi-donut">
                                        <vc-donut :size="kpiSize" :thickness="12" :start-angle="225" :total="100"
                                            :sections="getKpiSections(metric.value)" :foreground="kpiTrack"
                                            :background="kpiBg">
                                            <div class="kpi-center">
                                                <div class="kpi-value">
                                                    {{ metric.value.toFixed(2) }}
                                                </div>
                                                <div class="kpi-percent">%</div>
                                            </div>
                                        </vc-donut>
                                    </div>
                                </div>
                            </b-card>
                        </div>
                    </div>

                    <!-- Charts side-by-side -->

                    <b-row class="chart-row">
                        <b-col cols="12" md="8" class="mb-1">
                            <b-card class="chart-card h-100">
                                <div class="chart-header">OEE por Recurso</div>

                                <app-echart-bar :optionData="barOption" :height="barHeight" />
                            </b-card>
                        </b-col>

                        <b-col cols="12" md="4" class="mb-1">
                            <b-card class="chart-card h-100">
                                <div class="chart-header">OEE por Data</div>

                                <app-echart-stacked-area :optionData="lineOption" :height="lineHeight" />
                            </b-card>
                        </b-col>
                    </b-row>
                    <b-row class="chart-row">
                        <b-col cols="12" md="12" class="mb-1">
                            <b-card class="chart-card h-100">
                                <div class="chart-header">Performance por Recurso</div>

                                <app-echart-bar :optionData="performancePerResourceOption" :height="barHeight" />
                            </b-card>
                        </b-col>
                    </b-row>
                    <b-row class="chart-row">
                        <b-col cols="12" md="6" class="mb-1">
                            <b-card class="chart-card h-100">
                                <div class="chart-header">Paradas por Recurso</div>

                                <app-echart-bar :optionData="stopsByResourceOption" :height="barHeight" />
                            </b-card>
                        </b-col>
                        <b-col cols="12" md="6" class="mb-1">
                            <b-card class="chart-card h-100">
                                <div class="chart-header">Qualidade por Recurso</div>

                                <app-echart-bar :optionData="qualityByResourceOption" :height="barHeight" />
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="chart-row">
                        <b-col cols="12" class="mb-1">
                            <b-card class="table-card h-100">
                                <div class="chart-header d-flex justify-content-between align-items-center">
                                    <span>Tabela de OEE Geral</span>
                                    <b-button class="btn-filters" variant="outline-primary" size="sm"
                                        @click="exportOeeCsv">
                                        Exportar CSV
                                    </b-button>
                                </div>
                                <div class="table-wrapper">
                                    <b-table small striped hover :fields="oeeTableFields" :items="oeeTableItems"
                                        :per-page="oeeTablePageSize" :current-page="oeeTableCurrentPage"
                                        head-variant="light" class="oee-table" responsive />
                                </div>
                                <div v-if="oeeTableItems.length > oeeTablePageSize"
                                    class="d-flex justify-content-end mt-50">
                                    <b-pagination v-model="oeeTableCurrentPage" :total-rows="oeeTableItems.length"
                                        :per-page="oeeTablePageSize" size="sm" align="right" />
                                </div>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="chart-row">
                        <b-col cols="12" class="mb-1">
                            <b-card class="table-card h-100">
                                <div class="chart-header d-flex justify-content-between align-items-center">
                                    <span>Tabela de OEE por Recurso</span>
                                    <b-button class="btn-filters" variant="outline-primary" size="sm"
                                        @click="exportOeeByResourceCsv">
                                        Exportar CSV
                                    </b-button>
                                </div>
                                <div class="table-wrapper">
                                    <b-table small striped hover :fields="oeeByResourceTableFields"
                                        :items="oeeByResourceTableItems" :per-page="oeeTablePageSize"
                                        :current-page="oeeByResourceTableCurrentPage" head-variant="light"
                                        class="oee-table" responsive>
                                        <template #cell(actions)="data">
                                            <div class="d-flex align-items-center justify-content-center">
                                                <b-button
                                                    variant="flat-primary"
                                                    size="sm"
                                                    class="btn-icon rounded-circle"
                                                    v-b-tooltip.hover
                                                    title="Ver Detalhes do Cálculo"
                                                    @click="openResourceDetails(data.item)"
                                                >
                                                    <feather-icon icon="EyeIcon" size="16" />
                                                </b-button>
                                            </div>
                                        </template>
                                    </b-table>
                                </div>
                                <div v-if="oeeByResourceTableItems.length > oeeTablePageSize"
                                    class="d-flex justify-content-end mt-50">
                                    <b-pagination v-model="oeeByResourceTableCurrentPage"
                                        :total-rows="oeeByResourceTableItems.length" :per-page="oeeTablePageSize"
                                        size="sm" align="right" />
                                </div>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </div>

        <!-- Sidebar: Filters -->

        <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
            <template #header>
                <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
                    <span class="font-weight-bold">Filtros</span>

                    <span style="cursor: pointer" @click="filtersOpen = false">
                        <feather-icon icon="XIcon" size="18" />
                    </span>
                </div>
            </template>

            <div class="filters-card">
                <div class="filter">
                    <label class="filter-label">Filtro Rápido:</label>
                    <div class="quick-filter-buttons">
                        <button v-for="days in quickFilterDays" :key="days"
                            :class="['quick-filter-btn', { active: filterDays === days }]"
                            @click="setQuickFilter(days)">
                            {{ days }} dias
                        </button>
                    </div>
                </div>
                <div class="filter">
                    <label class="filter-label">De:</label>
                    <input type="date" v-model="startDate" class="date-input-sidebar" @change="onManualDateChange" />
                </div>
                <div class="filter">
                    <label class="filter-label">Até:</label>
                    <input type="date" v-model="endDate" class="date-input-sidebar" @change="onManualDateChange" />
                </div>
                <div class="filter" v-for="f in visibleFilters" :key="f.key">
                    <label class="filter-label">{{ f.label }}</label>

                    <b-form-select v-model="f.value" :options="f.options" size="sm" />
                </div>
            </div>

            <template #footer>
                <div class="d-flex justify-content-end px-1 pb-1">
                    <b-button size="sm" class="mr-50" variant="outline-secondary"
                        @click="clearFilters">Limpar</b-button>

                    <b-button size="sm" variant="primary" @click="applyFilters">Aplicar</b-button>
                </div>
            </template>
        </b-sidebar>

        <!-- Modal de Detalhamento do OEE -->
        <oee-details-modal
            v-model="detailsModalOpen"
            :title="selectedTitle"
            :oee="selectedDetails.oee"
            :available-component="selectedDetails.availableComponent"
            :performance-component="selectedDetails.performanceComponent"
            :quality-component="selectedDetails.qualityComponent"
            :details="selectedDetails.details"
            :initial-tab="selectedInitialTab"
        />
    </div>
</template>

<script>
import { BRow, BCol, BCard, BFormSelect, BButton, BButtonGroup, BSidebar, BTable, BPagination } from "bootstrap-vue";

import { mapMutations } from "vuex";

import AppEchartBar from "@core/components/charts/echart/AppEchartBar.vue";

import { useFilters } from "@/views/hooks/useFilters";

import AppEchartStackedArea from "@core/components/charts/echart/AppEchartStackedArea.vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import { loadResources } from '@/views/utils/getResources';
import OeeDetailsModal from "./OeeDetailsModal.vue";

export default {
    name: "OEEPage",

    components: {
        BRow,
        BCol,
        BCard,
        BFormSelect,
        BButton,
        BButtonGroup,
        BSidebar,
        BTable,
        BPagination,
        AppEchartBar,
        AppEchartStackedArea,
        FeatherIcon,
        OeeDetailsModal,
    },

    data() {
        const filtersHelper = useFilters();

        return {
            kpiTrack: "#E9ECEF",
            kpiBg: "#ffffff",
            filtersOpen: false,

            // Estado para detalhamento do OEE
            detailsModalOpen: false,
            selectedTitle: "",
            selectedInitialTab: "oee",
            selectedDetails: {
                oee: 0,
                availableComponent: 0,
                performanceComponent: 0,
                qualityComponent: 0,
                details: {}
            },
            oeeGlobalData: {},
            oeeByResourceOriginal: [],

            windowWidth: typeof window !== "undefined" ? window.innerWidth : 1440,

            dateRangeText: '',

            hasError: false,

            startDate: '',

            endDate: '',

            filterDays: 7,

            quickFilterDays: [7, 30, 60, 90],

            filtersHelper,

            filters: filtersHelper.createFilters(),
            resourceMap: {}, // Mapeamento de nome do recurso para ID
            areaMap: {},
            colorPalette: [
                '#f3811a',
                '#28c76f',
                '#ea5455',
                '#00cfe8',
                '#7367f0',
                '#ff9f43',
                '#1e90ff',
                '#9c27b0',
                '#2ecc71',
                '#34495e'
            ],
            resourceColors: {},

            kpis: [
                { key: "disponibilidade", title: "Disponibilidade (%)", value: 0 },
                { key: "qualidade", title: "Qualidade (%)", value: 0 },
                { key: "performance", title: "Performance (%)", value: 0 },
                { key: "oee", title: "OEE (%)", value: 0 },
                // { key: "oeePrevisto", title: "OEE Previsto (%)", value: 63.9 }
            ],

            barOption: {
                legend: {
                    left: 10,
                    top: 6,
                    orient: "horizontal",
                    itemWidth: 12,
                    itemHeight: 8,
                    itemGap: 8,
                    icon: "roundRect",
                    textStyle: { fontSize: 12 }
                },
                grid: {
                    left: "40px",
                    right: "10px",
                    bottom: "20px",
                    top: 50,
                    containLabel: true
                },
                xAxis: {
                    type: "category",
                    axisLabel: { rotate: 45, fontSize: 10 },
                    data: []
                },
                yAxis: { type: "value", splitLine: { show: false } },
                series: [
                    {
                        name: "OEE",
                        type: "bar",
                        data: [],
                        barMaxWidth: 40,
                        itemStyle: { color: "#f3811a" }
                    }
                ]
            },

            lineOption: {
                xAxisData: [],
                series: [
                    {
                        name: "OEE Calculado",
                        type: "line",
                        smooth: true,
                        showSymbol: false,
                        data: [],
                        lineStyle: { color: "#b15b1b" },
                    },
                    // {
                    //     name: "OEE Previsto",
                    //     type: "line",
                    //     smooth: true,
                    //     showSymbol: false,
                    //     data: [],
                    //     lineStyle: { color: "#f3811a" }
                    // }
                ]
            },

            performancePerResourceOption: {
                legend: {
                    left: 10,
                    top: 6,
                    orient: "horizontal",
                    itemWidth: 12,
                    itemHeight: 8,
                    itemGap: 8,
                    icon: "roundRect",
                    textStyle: { fontSize: 12 }
                },
                grid: {
                    left: "40px",
                    right: "10px",
                    bottom: "20px",
                    top: 50,
                    containLabel: true
                },
                xAxis: {
                    type: "category",
                    axisLabel: { rotate: 45, fontSize: 10 },
                    data: []
                },
                yAxis: { type: "value", splitLine: { show: false } },
                series: [
                    { name: "Performance", type: "bar", data: [], itemStyle: { color: "#f3811a" }, barMaxWidth: 40 }
                ]
            },

            stopsByResourceOption: {
                grid: { left: "50px", right: "20px", bottom: "60px", top: "40px", containLabel: true },
                xAxis: {
                    type: "category",
                    axisLabel: { rotate: 45, fontSize: 10 },
                    data: []
                },
                yAxis: { type: "value", splitLine: { show: false } },
                series: [{ name: "Tempo", type: "bar", data: [], barMaxWidth: 40 }]
            },

            qualityByResourceOption: {
                grid: { left: "50px", right: "20px", bottom: "60px", top: "40px", containLabel: true },
                xAxis: {
                    type: "category",
                    axisLabel: { rotate: 45, fontSize: 10 },
                    data: []
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    max: 100,
                    axisLabel: { formatter: "{value}%" },
                    splitLine: { show: false }
                },
                series: [{ name: "Qualidade (%)", type: "bar", data: [], barMaxWidth: 40 }]
            },

            oeeTableFields: [
                { key: "data", label: "Data", sortable: true },
                { key: "oee", label: "OEE (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "disponibilidade", label: "Disponibilidade (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "performance", label: "Performance (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "qualidade", label: "Qualidade (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") }
            ],
            oeeTableItems: [],
            oeeTableCurrentPage: 1,

            oeeByResourceTableFields: [
                { key: "recurso", label: "Recurso", sortable: true },
                { key: "oee", label: "OEE (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "disponibilidade", label: "Disponibilidade (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "performance", label: "Performance (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "qualidade", label: "Qualidade (%)", sortable: true, formatter: (v) => (v != null ? Number(v).toFixed(2) : "-") },
                { key: "actions", label: "Ações", sortable: false }
            ],
            oeeByResourceTableItems: [],
            oeeByResourceTableCurrentPage: 1
        };
    },

    mounted() {
        this.initializeDates();
        this.loadStoredResourceColors();
        this.loadOeeData();

        if (typeof window !== "undefined") {
            this._resizeHandler = () => (this.windowWidth = window.innerWidth);

            window.addEventListener("resize", this._resizeHandler);
        }
    },

    beforeDestroy() {
        if (this._resizeHandler) window.removeEventListener("resize", this._resizeHandler);
    },

    computed: {
        visibleFilters() {
            return this.filters.filter(f => !['year', 'month', 'day'].includes(f.key));
        },
        kpiSize() {
            const w = this.windowWidth;

            if (w >= 1400) return 92;

            if (w >= 1200) return 82;

            if (w >= 992) return 72;

            if (w >= 768) return 62;

            return 56;
        },

        barHeight() {
            const w = this.windowWidth;

            if (w >= 1400) return 420;

            if (w >= 1200) return 380;

            if (w >= 992) return 340;

            if (w >= 768) return 300;

            return 260;
        },

        lineHeight() {
            const w = this.windowWidth;

            if (w >= 1400) return 420;

            if (w >= 1200) return 380;

            if (w >= 992) return 340;

            if (w >= 768) return 300;

            return 260;
        },

        ticks() {
            const ticks = [];
            const startAngle = 225;
            const arcLength = 270;
            for (let i = 0; i <= 100; i += 2) {
                const angle = startAngle + (i / 100) * arcLength;
                ticks.push({ value: i, angle });
            }
            return ticks;
        },

        donutCenter() {
            return {
                x: this.kpiSize / 2,
                y: this.kpiSize / 2
            };
        },

        oeeTablePageSize() {
            const w = this.windowWidth;
            if (w >= 1600) return 8;
            if (w >= 1366) return 7;
            if (w >= 1200) return 6;
            if (w >= 992) return 5;
            return 4;
        }
    },

    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        async loadOeeData() {
            this.UPDATE_FLAG_SHOW_OVERLAY(true);

            const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

            if (!siteId) {
                console.warn("Nenhum site selecionado para carregar dados de OEE.");
                this.UPDATE_FLAG_SHOW_OVERLAY(false);
                return;
            }

            try {
                const params = { SiteId: siteId };
                this.loadWorkcenters();
                this.loadResourcesList(siteId);
                // Adicionar FilterDays se estiver definido
                if (this.filterDays) {
                    params.FilterDays = this.filterDays;
                }

                // Adicionar datas se estiverem definidas
                if (this.startDate && this.endDate) {
                    params.StartDate = this.startDate;
                    params.EndDate = this.endDate;
                }

                // Adicionar filtro de recurso se selecionado query parameter
                const selectedResource = this.filtersHelper.getFilterValue(this.filters, 'resource');
                if (selectedResource && selectedResource !== 'Todos' && this.resourceMap[selectedResource]) {
                    params.ResourcesIds = String(this.resourceMap[selectedResource]);
                }

                // Adicionar filtro de área se selecionado query parameter
                const selectedArea = this.filtersHelper.getFilterValue(this.filters, 'area');
                // console.log('selectedArea', selectedArea);
                if (selectedArea && selectedArea !== 'Todos' && this.areaMap[selectedArea]) {
                    params.WorkcentersIds = String(this.areaMap[selectedArea]);
                }

                // Atualizar o texto do range de datas
                this.updateDateRangeText();

                const [oeeRes] = await Promise.all([
                    this.$http2.get("/api/oee/calculate", { params })
                ]);

                this.applyOeeResponse(oeeRes?.data?.data || {});
                this.updateStopsByResourceChart(oeeRes?.data?.data?.stopsByResource || []);
                this.updateQualityByResourceChart(oeeRes?.data?.data?.qualityByResource || []);
                this.hasError = false;
            } catch (error) {
                console.error("Erro ao carregar dados do OEE", error);
                this.hasError = true;
            } finally {
                this.UPDATE_FLAG_SHOW_OVERLAY(false);
            }
        },

        // TODO: AQUI QUANDO SUBIR PRA PROD VAI PUXAR DA FUNCAO GETWORKCENTERS
        async loadWorkcenters() {
            const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

            if (!siteId) {
                console.warn("Nenhum site selecionado para carregar dados de OEE.");
                return;
            }

            const workcenters = await this.$http2.get("/api/myconfig/workcenters", {
                params: {
                    SiteId: siteId
                }
            });

            // console.log('workcenters', workcenters);

            const areaNames = workcenters?.data?.data?.map(item => item?.workcenterName) || [];
            this.filtersHelper.updateFilterOptions(this.filters, 'area', areaNames, true);

            // setando o map de areas
            this.areaMap = Object.fromEntries(workcenters?.data?.data?.map(item => [item.workcenterName, item.workcenterId]) || []);
        },

        async loadResourcesList(siteId) {
            const sid = siteId ?? this.filtersHelper.getCurrentSiteId(this.$cookies);
            const resolvedId = typeof sid === "object" ? (sid?.id ?? sid?.siteId) : sid;
            const resources = await loadResources(resolvedId, this.$http2);
            // console.log('resources', resources);
            const resourcesNames = resources?.map(item => item?.name) || [];
            // console.log('resourcesNames', resourcesNames);
            this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourcesNames, true);

            // setando o map de recursos
            this.resourceMap = Object.fromEntries(resources?.map(item => [item.name, item.id]) || []);
        },

        applyOeeResponse(payload = {}) {
            const {
                oeeGlobal = {},
                oeeByResource = [],
                oeeByReferenceDate = []
            } = payload || {};

            // Salvar dados originais para detalhamento
            this.oeeGlobalData = {
                oee: oeeGlobal.oee ?? 0,
                availableComponent: oeeGlobal.availableComponent ?? 0,
                performanceComponent: oeeGlobal.performanceComponent ?? 0,
                qualityComponent: oeeGlobal.qualityComponent ?? 0,
                details: oeeGlobal.details || {}
            };
            this.oeeByResourceOriginal = oeeByResource || [];


            // calculando o oee corretamente
            // const calculatedOee = ((oeeGlobal.availableComponent / 100) * (oeeGlobal.qualityComponent / 100) * (oeeGlobal.performanceComponent / 100));


            this.setKpiValue("disponibilidade", this.toPercent(oeeGlobal.availableComponent));
            this.setKpiValue("qualidade", this.toPercent(oeeGlobal.qualityComponent));
            this.setKpiValue("performance", this.toPercent(oeeGlobal.performanceComponent));
            this.setKpiValue("oee", this.toPercent(oeeGlobal.oee));

            // Construir resourceMap (nome -> ID) para enviar na próxima requisição
            if (Array.isArray(oeeByResource)) {
                oeeByResource.forEach(item => {
                    if (item?.resourceName && item?.resourceId) {
                        this.resourceMap[item.resourceName] = item.resourceId;
                    }
                });
            }
            const resourceLabels = Array.isArray(oeeByResource)
                ? oeeByResource.map(
                    (item, index) =>
                        item?.resourceName ||
                        item?.resource ||
                        item?.name ||
                        item?.id ||
                        `Recurso ${index + 1}`
                )
                : [];
            const resourceValues = Array.isArray(oeeByResource)
                ? oeeByResource.map((item) =>
                    this.toPercent(item?.oee ?? item?.value ?? item?.oeeValue)
                )
                : [];

            this.updateBarChart(resourceLabels, resourceValues);

            const performancePerResourceLabels = Array.isArray(oeeByResource)
                ? oeeByResource.map((item, index) =>
                    item?.resourceName ||
                    `Recurso ${index + 1}`
                )
                : [];
            const performancePerResourceValues = Array.isArray(oeeByResource) ? oeeByResource.map((item) =>
                this.toPercent(item?.performanceComponent ?? 100)
            ) : [];
            // console.log('performancePerResourceLabels', performancePerResourceLabels);
            // console.log('performancePerResourceValues', performancePerResourceValues);
            this.updatePerformancePerResourceChart(performancePerResourceLabels, performancePerResourceValues);
            const referenceLabels = Array.isArray(oeeByReferenceDate)
                ? oeeByReferenceDate.map((item, index) =>
                    this.formatReferenceLabel(item, index)
                )
                : [];
            const referenceValues = Array.isArray(oeeByReferenceDate)
                ? oeeByReferenceDate.map((item) =>
                    this.toPercent(item?.oee ?? item?.value ?? item?.oeeValue)
                )
                : [];

            this.updateLineChart(referenceLabels, referenceValues);
            this.updateOeeTable(oeeByReferenceDate, oeeGlobal);
            this.updateOeeByResourceTable(oeeByResource, oeeGlobal);
        },

        updateOeeTable(oeeByReferenceDate = [], oeeGlobal = {}) {
            if (!Array.isArray(oeeByReferenceDate) || oeeByReferenceDate.length === 0) {
                this.oeeTableItems = [];
                this.oeeTableCurrentPage = 1;
                return;
            }

            const globalAvail = this.toPercent(oeeGlobal?.availableComponent ?? 0);
            const globalQual = this.toPercent(oeeGlobal?.qualityComponent ?? 0);
            const globalPerf = this.toPercent(oeeGlobal?.performanceComponent ?? 0);

            this.oeeTableItems = oeeByReferenceDate.map((item) => {
                const avail = this.toPercent(item?.availableComponent ?? item?.availabilityComponent ?? globalAvail);
                const qual = this.toPercent(item?.qualityComponent ?? item?.quality ?? globalQual);
                const perf = this.toPercent(item?.performanceComponent ?? globalPerf);
                const oee = this.toPercent(item?.oee ?? item?.value ?? item?.oeeValue)
                    || (avail / 100) * (qual / 100) * (perf / 100) * 100;

                const dateRaw = item?.referenceDate ?? item?.date ?? item?.reference ?? item?.period ?? "";
                const dataLabel = dateRaw ? this.formatTableDate(dateRaw) : "-";

                return {
                    data: dataLabel,
                    oee,
                    disponibilidade: avail,
                    performance: perf,
                    qualidade: qual
                };
            });
            this.oeeTableCurrentPage = 1;
        },

        formatTableDate(dateVal) {
            if (!dateVal) return "-";
            const d = new Date(dateVal);
            if (Number.isNaN(d.getTime())) return String(dateVal);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        },

        updateOeeByResourceTable(oeeByResource = [], oeeGlobal = {}) {
            if (!Array.isArray(oeeByResource) || oeeByResource.length === 0) {
                this.oeeByResourceTableItems = [];
                this.oeeByResourceTableCurrentPage = 1;
                return;
            }

            const globalAvail = this.toPercent(oeeGlobal?.availableComponent ?? 0);
            const globalQual = this.toPercent(oeeGlobal?.qualityComponent ?? 0);

            this.oeeByResourceTableCurrentPage = 1;
            this.oeeByResourceTableItems = oeeByResource.map((item, index) => ({
                id: item?.resourceId,
                recurso: item?.resourceName || item?.resource || item?.name || item?.id || `Recurso ${index + 1}`,
                oee: this.toPercent(item?.oee ?? item?.value ?? item?.oeeValue),
                disponibilidade: this.toPercent(item?.availableComponent ?? item?.availabilityComponent ?? globalAvail),
                performance: this.toPercent(item?.performanceComponent ?? 100),
                qualidade: this.toPercent(item?.qualityComponent ?? item?.quality ?? globalQual)
            }));
        },

        exportOeeCsv() {
            const headers = this.oeeTableFields.map(f => f.label);
            const keys = this.oeeTableFields.map(f => f.key);
            const rows = this.oeeTableItems.map(item =>
                keys.map(k => {
                    const v = item[k];
                    return typeof v === "number" ? v.toFixed(2) : (v ?? "");
                })
            );
            const csv = this.buildCsv([headers, ...rows], ";");
            const now = new Date().toISOString().split("T")[0];
            this.downloadCsv(csv, `oee_geral_${now}.csv`);
        },

        exportOeeByResourceCsv() {
            const headers = this.oeeByResourceTableFields.map(f => f.label);
            const keys = this.oeeByResourceTableFields.map(f => f.key);
            const rows = this.oeeByResourceTableItems.map(item =>
                keys.map(k => {
                    const v = item[k];
                    return typeof v === "number" ? v.toFixed(2) : (v ?? "");
                })
            );
            const csv = this.buildCsv([headers, ...rows], ";");
            const now = new Date().toISOString().split("T")[0];
            this.downloadCsv(csv, `oee_por_recurso_${now}.csv`);
        },

        buildCsv(matrix, sep = ",") {
            return matrix
                .map(row =>
                    row
                        .map(v => {
                            const val = v == null ? "" : String(v);
                            const needsQuote = /[",\n;]/.test(val) || val.includes(sep);
                            const escaped = val.replace(/"/g, '""');
                            return needsQuote ? `"${escaped}"` : escaped;
                        })
                        .join(sep)
                )
                .join("\n");
        },

        downloadCsv(csvContent, filename) {
            const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },
        setKpiValue(key, value) {
            const metric = this.kpis.find((item) => item.key === key);

            if (!metric) {
                return;
            }

            metric.value = Number.isFinite(value) ? value : 0;
        },
        toPercent(value) {
            const numeric = Number(value);

            if (!Number.isFinite(numeric)) {
                return 0;
            }

            const scaled = Math.abs(numeric) <= 1 ? numeric * 100 : numeric;

            const result = Math.max(0, scaled);
            return Number(result.toFixed(2));
        },
        updateBarChart(labels = [], values = []) {
            const targetLabels = Array.isArray(labels) ? labels : [];
            const targetValues = Array.isArray(values) ? values : [];

            this.barOption.xAxis.data.splice(
                0,
                this.barOption.xAxis.data.length,
                ...targetLabels
            );

            const series = this.barOption.series?.[0];

            if (series && Array.isArray(series.data)) {
                const coloredData = targetLabels.map((label, index) => ({
                    value: targetValues[index] ?? 0,
                    itemStyle: { color: this.getResourceColor(label) }
                }));
                series.data.splice(0, series.data.length, ...coloredData);
            }
        },
        updateLineChart(labels = [], actualValues = []) {
            const axisLabels = Array.isArray(labels) ? labels : [];
            const actual = Array.isArray(actualValues) ? actualValues : [];

            this.lineOption.xAxisData.splice(
                0,
                this.lineOption.xAxisData.length,
                ...axisLabels
            );

            const seriesCalculated = this.lineOption.series?.[0];
            const seriesForecast = this.lineOption.series?.[1];

            if (seriesCalculated && Array.isArray(seriesCalculated.data)) {
                seriesCalculated.data.splice(0, seriesCalculated.data.length, ...actual);
            }

            if (seriesForecast && Array.isArray(seriesForecast.data)) {
                const forecast = axisLabels.map(() => 63.9);
                seriesForecast.data.splice(0, seriesForecast.data.length, ...forecast);
            }
        },
        formatReferenceLabel(entry, index) {
            if (!entry) {
                return `Periodo ${index + 1}`;
            }

            const candidate =
                entry.referenceDate ||
                entry.date ||
                entry.reference ||
                entry.period ||
                entry.name ||
                entry.label ||
                null;

            if (candidate) {
                const date = new Date(candidate);

                if (!Number.isNaN(date.getTime())) {
                    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
                }

                return String(candidate);
            }

            if (entry.periodStart && entry.periodEnd) {
                return `${entry.periodStart} - ${entry.periodEnd}`;
            }

            return `Periodo ${index + 1}`;
        },
        safeJSONParse(value) {
            try {
                return value ? JSON.parse(value) : null;
            } catch (error) {
                return null;
            }
        },
        applyFilters() {
            this.filtersOpen = false;
            this.loadOeeData();
        },
        clearFilters() {
            this.filtersHelper.clearFilters(this.filters);
            this.initializeDates();
            this.loadOeeData();
        },

        initializeDates() {
            // Inicializar com os últimos 7 dias
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);

            this.startDate = this.formatDateToISO(startDate);
            this.endDate = this.formatDateToISO(endDate);
        },

        formatDateToISO(date) {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },

        updateDateRangeText() {
            if (!this.startDate || !this.endDate) {
                const endDate = new Date();
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - 7);

                this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
            } else {
                const startDate = this.parseLocalDate(this.startDate);
                const endDate = this.parseLocalDate(this.endDate);

                this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
            }
        },

        parseLocalDate(dateStr) {
            if (!dateStr) return null;
            if (dateStr instanceof Date) return new Date(dateStr.getTime());
            const str = String(dateStr);
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                const [y, m, d] = str.split('-').map(Number);
                return new Date(y, m - 1, d);
            }
            return new Date(dateStr);
        },

        updatePerformancePerResourceChart(labels = [], values = []) {
            const axisLabels = Array.isArray(labels) ? labels : [];
            const actualValues = Array.isArray(values) ? values : [];

            this.performancePerResourceOption.xAxis.data.splice(
                0,
                this.performancePerResourceOption.xAxis.data.length,
                ...axisLabels
            );

            const performancePerResource = this.performancePerResourceOption.series?.[0];

            if (performancePerResource && Array.isArray(performancePerResource.data)) {
                const coloredData = axisLabels.map((label, index) => ({
                    value: actualValues[index] ?? 0,
                    itemStyle: { color: this.getResourceColor(label) }
                }));
                performancePerResource.data.splice(0, performancePerResource.data.length, ...coloredData);
            }
        },

        updateStopsByResourceChart(stopEventsByResource = []) {
            if (!Array.isArray(stopEventsByResource) || stopEventsByResource.length === 0) {
                this.stopsByResourceOption.xAxis.data = [];
                this.stopsByResourceOption.series[0].data = [];
                this.stopsByResourceOption.yAxis.axisLabel = undefined;
                this.stopsByResourceOption.tooltip = undefined;
                return;
            }

            const resourceLabels = stopEventsByResource.map(item => item?.resourceName || "Sem nome");
            const valuesMinutes = stopEventsByResource.map(item => Number(item?.totalTime || 0));
            const converted = this.convertStopsChartValues(valuesMinutes);

            const coloredData = resourceLabels.map((label, index) => ({
                value: converted.values[index] ?? 0,
                itemStyle: { color: this.getResourceColor(label) }
            }));

            this.stopsByResourceOption.xAxis.data = resourceLabels;
            this.stopsByResourceOption.series[0].data = coloredData;
            this.stopsByResourceOption.yAxis.axisLabel = {
                formatter: (v) => (converted.isConverted ? Math.round(v).toString() : Math.round(v).toString())
            };
            this.stopsByResourceOption.tooltip = {
                trigger: "axis",
                formatter: (params) => {
                    const p = Array.isArray(params) ? params[0] : params;
                    const idx = p?.dataIndex ?? 0;
                    const orig = converted.originalValues[idx] ?? 0;
                    const timeStr = this.formatStopsTooltipTime(orig);
                    return `${p?.name || ""}<br/>${p?.marker || ""}Tempo: ${timeStr}`;
                }
            };
        },

        convertStopsChartValues(valuesInMinutes) {
            const safe = valuesInMinutes.map(v => Number(v));
            const maxVal = Math.max(0, ...safe);
            const toHours = maxVal >= 60;

            return toHours
                ? {
                    values: safe.map(v => v / 60),
                    originalValues: safe,
                    isConverted: true
                }
                : {
                    values: safe,
                    originalValues: safe,
                    isConverted: false
                };
        },

        formatStopsTooltipTime(totalMinutes) {
            const m = Number(totalMinutes);
            if (m >= 60) {
                const h = Math.floor(m / 60);
                const min = Math.round(m % 60);
                return `${h}h ${min}min`;
            }
            return `${Math.round(m)}min`;
        },

        updateQualityByResourceChart(qualityByResource = []) {
            if (!Array.isArray(qualityByResource) || qualityByResource.length === 0) {
                this.qualityByResourceOption.xAxis.data = [];
                this.qualityByResourceOption.series[0].data = [];
                return;
            }

            const resourceLabels = qualityByResource.map((item, i) =>
                item?.resourceName || `Recurso ${i + 1}`
            );

            const qualityPercents = qualityByResource.map((item) => Number(item?.quality ?? 0));

            const coloredData = resourceLabels.map((label, index) => ({
                value: Number(qualityPercents[index]?.toFixed(2) ?? 0),
                itemStyle: { color: this.getResourceColor(label) }
            }));

            this.qualityByResourceOption.xAxis.data = resourceLabels;
            this.qualityByResourceOption.series[0].data = coloredData;
        },
        formatDateToBrazilian(date) {
            if (!date) return '';

            const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
                ? this.parseLocalDate(date)
                : new Date(date);
            if (!d || Number.isNaN(d.getTime())) return '';

            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();

            return `${day}-${month}-${year}`;
        },

        setQuickFilter(days) {
            this.filterDays = days;

            // Atualizar as datas baseado no filtro rápido
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - days);

            this.startDate = this.formatDateToISO(startDate);
            this.endDate = this.formatDateToISO(endDate);
        },

        onManualDateChange() {
            // Quando o usuário alterar manualmente as datas, limpar o filterDays
            this.filterDays = null;
        },

        getKpiColor(value) {
            if (value < 80) {
                return '#ea5455';
            } else if (value >= 80 && value < 90) {
                return '#ff9f43';
            } else {
                return '#28c76f';
            }
        },

        getKpiSections(value) {
            const visualValue = Math.min(value, 100);
            const normalizedValue = (visualValue / 100) * 75;
            const emptyValue = 75 - normalizedValue;
            const gapValue = 25;

            return [
                { value: normalizedValue, color: this.getKpiColor(value) },
                { value: emptyValue, color: this.kpiTrack },
                { value: gapValue, color: this.kpiBg }
            ];
        },

        getTickX1(angle, size) {
            const radius = size / 2;
            const centerX = size / 2;
            const centerY = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            return centerX + (radius + 6) * Math.cos(rad);
        },

        getTickY1(angle, size) {
            const radius = size / 2;
            const centerX = size / 2;
            const centerY = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            return centerY + (radius + 6) * Math.sin(rad);
        },

        getTickLength(value) {
            if (value % 10 === 0) {
                return 8;
            }
            return 4;
        },

        getTickX2(angle, size, value) {
            const radius = size / 2;
            const centerX = size / 2;
            const centerY = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            const tickLength = this.getTickLength(value);
            return centerX + (radius + 6 + tickLength) * Math.cos(rad);
        },

        getTickY2(angle, size, value) {
            const radius = size / 2;
            const centerX = size / 2;
            const centerY = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            const tickLength = this.getTickLength(value);
            return centerY + (radius + 6 + tickLength) * Math.sin(rad);
        },

        getTickColor(tickValue) {
            return this.getKpiColor(tickValue);
        },

        loadStoredResourceColors() {
            if (typeof window === 'undefined') return;
            const stored = this.safeJSONParse(localStorage.getItem('oeeResourceColors'));
            this.resourceColors = stored && typeof stored === 'object' ? stored : {};
        },
        saveResourceColors() {
            if (typeof window === 'undefined') return;
            localStorage.setItem('oeeResourceColors', JSON.stringify(this.resourceColors));
        },
        getResourceColor(resourceName) {
            const key = resourceName || 'default';
            if (this.resourceColors[key]) {
                return this.resourceColors[key];
            }
            const usedColors = new Set(Object.values(this.resourceColors));
            const available = this.colorPalette.filter(color => !usedColors.has(color));
            const pool = available.length ? available : this.colorPalette;
            const color = pool[Math.floor(Math.random() * pool.length)];
            this.resourceColors[key] = color;
            this.saveResourceColors();
            return color;
        },

        getTabKey(key) {
            if (key === "disponibilidade") return "availability";
            if (key === "qualidade") return "quality";
            return key;
        },
        openGlobalDetails(tab = "oee") {
            this.selectedDetails = {
                oee: this.oeeGlobalData.oee ?? 0,
                availableComponent: this.oeeGlobalData.availableComponent ?? 0,
                performanceComponent: this.oeeGlobalData.performanceComponent ?? 0,
                qualityComponent: this.oeeGlobalData.qualityComponent ?? 0,
                details: this.oeeGlobalData.details || {}
            };
            this.selectedTitle = "Geral / Consolidado";
            this.selectedInitialTab = tab;
            this.detailsModalOpen = true;
        },
        openResourceDetails(tableItem) {
            const resource = this.oeeByResourceOriginal.find(r => r.resourceId === tableItem.id);
            if (resource) {
                this.selectedDetails = {
                    oee: resource.oee ?? 0,
                    availableComponent: resource.availabilityComponent ?? resource.availableComponent ?? 0,
                    performanceComponent: resource.performanceComponent ?? 0,
                    qualityComponent: resource.qualityComponent ?? 0,
                    details: resource.details || {}
                };
                this.selectedTitle = resource.resourceName;
                this.selectedInitialTab = "oee";
                this.detailsModalOpen = true;
            }
        },

        getLabelX(angle, size) {
            const radius = size / 2;
            const centerX = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            const offsetX = angle === 225 ? -3 : 3;
            return centerX + (radius + 25) * Math.cos(rad) + offsetX;
        },

        getLabelY(angle, size) {
            const radius = size / 2;
            const centerY = size / 2;
            const rad = (angle - 90) * Math.PI / 180;
            return centerY + (radius + 35) * Math.sin(rad);
        }
    }
};
</script>

<style lang="scss" scoped>
#oee-page {
    .header-block {
        padding: 10px 14px;

        display: flex;

        justify-content: space-between;

        align-items: center;
    }

    .page-title {
        font-size: 18px;
        font-weight: 700;
        color: #4c4541;
    }

    .date-range-info {
        font-size: 12px;
        color: #6c757d;
        margin-left: 12px;
        font-weight: 500;
    }

    .error-info {
        font-size: 12px;
        color: #ea5455;
        margin-left: 12px;
        font-weight: 500;
        display: flex;
        align-items: center;
    }

    .body-block {
        padding: 4px 14px 20px 14px;
    }

    .filters-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 12px;

        .filter+.filter {
            margin-top: 8px;
        }

        .filter-label {
            display: block;
            font-weight: 600;
            font-size: 12px;
            margin-bottom: 6px;
        }

        .date-input-sidebar {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #d8d6de;
            border-radius: 4px;
            font-size: 14px;
            color: #6c757d;
            background-color: #fff;
            cursor: pointer;
            transition: border-color 0.2s;

            &:hover {
                border-color: #9f5714;
            }

            &:focus {
                outline: none;
                border-color: #9f5714;
                box-shadow: 0 0 0 2px rgba(243, 129, 26, 0.1);
            }
        }

        .quick-filter-buttons {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
        }

        .quick-filter-btn {
            padding: 6px 10px;
            border: 1px solid #d8d6de;
            border-radius: 4px;
            background-color: #fff;
            color: #6c757d;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
                border-color: #9f5714;
                color: #9f5714;
            }

            &.active {
                background-color: #9f5714;
                border-color: #9f5714;
                color: #fff;
            }
        }
    }

    .kpi-grid {
        display: grid;

        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));

        gap: 12px;

        margin-bottom: 16px;
    }

    .kpi-item {
        display: flex;
    }

    .kpi-card {
        text-align: center;

        padding-top: 10px;

        width: 100%;

        height: 100%;

        &.cursor-pointer {
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;

            &:hover {
                transform: translateY(-3px);
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
            }
        }
    }

    .kpi-title {
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    .kpi-donut-wrapper {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    .kpi-ticks {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        z-index: 0;
        overflow: visible;
    }

    .kpi-donut {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        z-index: 1;
    }

    .kpi-center {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: absolute;
        inset: 0;
    }

    .kpi-value {
        font-size: 18px;
        font-weight: 700;
        color: #4c4541;
        line-height: 1;
    }

    .kpi-percent {
        font-size: 14px;
        font-weight: 700;
        color: #4c4541;
        line-height: 1;
        margin-top: 2px;
    }

    .kpi-label {
        font-size: 11px;
        font-weight: 600;
        color: #6c757d;
        fill: #6c757d;
    }

    .kpi-label-zero {
        color: #ea5455;
        fill: #ea5455;
    }

    .kpi-label-hundred {
        color: #28c76f;
        fill: #28c76f;
    }

    .chart-row {
        margin-top: 10px;
    }

    .chart-card,
    .table-card {
        height: 100%;
        padding: 12px;
    }

    .table-card {
        display: flex;
        flex-direction: column;
        padding: 16px 16px 0 16px;
    }

    .table-wrapper {
        flex: 1 1 auto;
        margin-top: 8px;
        overflow-x: auto;
    }

    .table-card .d-flex {
        margin-top: 12px;
    }

    .oee-table {
        font-size: 12px;
    }

    .chart-header {
        font-weight: 700;
        margin-bottom: 4px;
    }

    .btn-filters {
        border-color: #9f5714 !important;
        color: #9f5714 !important;

        &:hover {
            background-color: #9f5714 !important;
            color: #fff !important;
        }
    }

    ::v-deep .b-sidebar-footer {
        .btn-primary {
            background-color: #9f5714 !important;
            border-color: #9f5714 !important;

            &:hover {
                background-color: #7d4510 !important;
                border-color: #7d4510 !important;
            }
        }
    }
}

@media (max-width: 767.98px) {
    #oee-page {
        .body-block {
            padding: 4px 8px 12px 8px;
        }

        .kpi-title {
            font-size: 11px;
        }

        .kpi-value {
            font-size: 13px;
        }

        .chart-header {
            font-size: 12px;
        }
    }
}

@media (max-height: 100vh) {
    #oee-page {
        .body-block {
            padding: 4px 14px 20px 14px;
        }

        .kpi-title {
            font-size: 11px;
        }

        .kpi-value {
            font-size: 13px;
        }

        .chart-header {
            font-size: 12px;
        }
    }
}
</style>
